import { css, etiColors } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import type { BoundNew as Bound, EventSegmentType } from '@eti/schema-types';
import { EventSegmentType as EventSegmentTypeValues } from '@eti/schema-types';
import { duration as durationUtil } from '@eti/utils';
import { getSegmentsFromBounds, getTimeBreakdown } from '../utils/format';
import EventIcon from './TripDetailsSegmentEventIcon';

const eventTypeItemStyles = css`
  align-items: center;
  color: ${etiColors.black};
  display: flex;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
`;

export type eventSegmentTypePropTypes =
  | typeof EventSegmentType.OvernightStay
  | typeof EventSegmentType.ChangeOfAirport
  | typeof EventSegmentType.TransferToAirport
  | typeof EventSegmentType.TransferToBus
  | typeof EventSegmentType.TransferToTrain;

interface TripDetailsEventSegmentInfoListProps {
  bounds: Bound[];
  changeOfAirportParameters: {
    fromAirport: string;
    toAirport: string;
  };
  className?: string;
  eventSegmentTypes: {
    legendNumber: number;
    type: eventSegmentTypePropTypes;
  }[];
  overnightStayParameter: string;
}
const TripDetailsSegmentEventInfoList = ({
  changeOfAirportParameters,
  className,
  eventSegmentTypes,
  overnightStayParameter,
  bounds,
}: TripDetailsEventSegmentInfoListProps) => {
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();
  const segments = getSegmentsFromBounds(bounds);
  const eventSegments = segments.filter((segment) => segment.__typename === 'EventSegment');
  const overnightStayDuration = eventSegments.find((eventSegment) => {
    return eventSegment.types.find(
      (eventSegmentType: { type: string }) =>
        eventSegmentType.type === EventSegmentTypeValues.OvernightStay,
    );
  });
  let overnightStayText = t('TripDetails.Details.Overnight.Next.Day');
  if (overnightStayDuration) {
    const mainDuration = durationUtil.getDuration(
      overnightStayDuration.duration,
      language.code,
      timeFormat,
    );
    const { days } = getTimeBreakdown(mainDuration);
    if (days >= 2) {
      overnightStayText = t('TripDetails.Details.Overnight.Total.Days', days);
    }
  }

  const description = {
    [EventSegmentTypeValues.OvernightStay]: t(
      'TripDetails.Details.Overnight.Continue',
      overnightStayParameter,
      overnightStayText,
    ),
    [EventSegmentTypeValues.ChangeOfAirport]: t(
      'TripDetails.Details.Description.ChangeOfAirport',
      changeOfAirportParameters?.fromAirport,
      changeOfAirportParameters?.toAirport,
    ),
    [EventSegmentTypeValues.TransferToAirport]: t(
      'TripDetails.Details.Description.TransferToAirport',
    ),
    [EventSegmentTypeValues.TransferToBus]: t('TripDetails.Details.Description.TransferToBus'),
    [EventSegmentTypeValues.TransferToTrain]: t('TripDetails.Details.Description.TransferToTrain'),
  };
  return (
    <ul className={className} data-testid="tripDetails-segment-eventTypes-list">
      {eventSegmentTypes.map(({ legendNumber, type }) => (
        <li className={eventTypeItemStyles} key={`${type}`}>
          <EventIcon legendNumber={legendNumber} />
          <span>{description[type]}</span>
        </li>
      ))}
    </ul>
  );
};

export default TripDetailsSegmentEventInfoList;
